import { css, styled } from "../../styles";

export const TableContainer = styled("div", {
  overflowY: "overlay",
  maxHeight: "100%",
  height: "100%",

  zIndex: 1000,

  borderRadius: "8px",

  width: "100%",
  minWidth: "100%",

  border: "1px solid #ECECEC",
  backgroundColor: "$white",

  boxSizing: "border-box",

  "&::-webkit-scrollbar": {
    width: "$4",
    height: "$4",
  },

  "&:hover": {
    "&::-webkit-scrollbar": {
      width: "$4",
      height: "$4",
      backgroundColor: "transparent",
    },

    "&::-webkit-scrollbar-track": {
      marginTop: "80px",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "$gray-50",
      borderRadius: "10px",
    },
  },

  variants: {
    emptyState: {
      true: {
        display: "flex",
        flexDirection: "column",
        height: "calc(100% - 68px)",
        maxHeight: "none",
        position: "relative",

        "@md": {
          height: "calc(100% - 47px)",
        },
      },
    },

    isProtocols: {
      true: {
        th: {
          paddingTop: "25px",
        },

        div: {
          p: {
            marginTop: "70px",
          },
        },
      },
    },

    isAdherence: {
      true: {
        height: "420px !important",
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,

        border: "none",
        borderTop: "1px solid $gray-30",
      },
    },
  },
});

export const TableElement = styled("table", {
  width: "100%",
  background: "$white",

  borderRadius: "$1",
  borderSpacing: "0px",
  borderCollapse: "collapse",
  borderLeft: "none",
  borderRight: "none",

  thead: {
    borderBottom: "2px solid $gray-20",
  },

  tfoot: {
    height: "26px",
  },
});

export const ThSorted = styled("th", {
  background: "$white",
  color: "$charcoal",

  textAlign: "left",
  fontFamily: "$openSans",
  fontStyle: "normal",
  fontSize: "16px",

  fontWeight: "600",

  paddingTop: "$13",
  paddingBottom: "$9",
  paddingRight: "$11",

  position: "sticky",
  top: "0px",

  "&:first-child": {
    paddingLeft: "$14",
  },

  "&:last-child": {
    paddingRight: "$14",
  },
  cursor: "pointer",
  width: "auto",
});

export const ThUnsorted = styled("th", {
  background: "$white",
  color: "$charcoal",

  textAlign: "left",
  fontFamily: "$openSans",
  fontStyle: "normal",
  fontSize: "16px",

  fontWeight: "600",

  paddingTop: "$13",
  paddingBottom: "$9",
  paddingRight: "$11",

  position: "sticky",
  top: "0px",

  "&:first-child": {
    paddingLeft: "$14",
  },

  "&:last-child": {
    paddingRight: "$14",
  },
  width: "auto",
});

export const Tr = styled("tr", {
  height: "42px",

  fontFamily: "$openSans",

  fontStyle: "normal",
  fontSize: "16px",
  fontWeight: "400",

  color: "$charcoal",

  td: {
    paddingRight: "$11",

    "&:first-child": {
      paddingLeft: "$14",
    },

    "&:last-child": {
      paddingRight: "$14",
    },
  },

  "&:nth-child(odd)": {
    backgroundColor: "$gray-20",
  },

  "&:hover": {
    backgroundColor: "var(--colors-table-row-hover-background)",
    transition: "background-color .1s",

    cursor: "pointer",

    td: {
      ".actionContainer": {
        visibility: "visible",
      },
    },
  },

  "&:active": {
    td: {
      ".actionContainer": {
        visibility: "visible",
      },
    },
  },

  "&:focus-visible": {
    outline: "none",
    backgroundColor: "var(--colors-table-row-focus-background)",
    boxShadow: "0px 0px 0px 2px var(--colors-table-row-focus-border)",
  },

  variants: {
    alert: {
      true: {
        "&:hover": {
          backgroundColor: "rgba(250, 209, 217, 0.14) !important",
        },
      },
    },
    dimmed: {
      true: {
        opacity: 0.3,
        transition: "opacity 120ms ease",
        "&:hover": { backgroundColor: "inherit !important" },
      },
    },
    highlighted: {
      true: {
        "&&": {
          backgroundColor: "var(--colors-table-row-hover-background)",
        },
      },
    },
  },
});

export const EmptyStateContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",

  // Fill the space left below the header row (the container is a flex column)
  // so the message centers in the middle of the table, not right under it.
  flex: 1,
  minHeight: 0,

  p: {
    fontFamily: "$openSans",

    fontSize: "$text-lg",
    fontWeight: 400,

    color: "$charcoal",
  },
});

export const tooltipStyles = {
  borderRadius: "4px !important",
  fontSize: "12px",
  fontWeight: 500,
  padding: "12px 16px",
  display: "flex",
  backgroundColor: "$charcoal",
};

// Row hover modal: same look as the header tooltip, but a bit wider so the
// text ("...cannot be edited yet") fits on a single line instead of wrapping.
export const rowTooltipStyles = {
  ...tooltipStyles,
  whiteSpace: "nowrap" as const,
};

export const ArrowContent = styled("div", {
  position: "relative",
  // Cancels the pill's own left padding so the header text stays aligned with
  // the row cells below it — keep the two in step.
  left: "-7px",
  // inline-block so a wrapping header (e.g. "No. of reads" in a narrow column)
  // gets a single, uniform rounded highlight across both lines instead of one
  // box per line. max-content sizes it to the widest line so the highlight
  // hugs the text instead of stretching to the cell width.
  display: "inline-block",
  width: "max-content",
  height: "auto",
  padding: "5px 7px 5px 7px",
  borderRadius: "5px",
  "&:hover": {
    background: "#EDEDED",
  },
});

export const isSorted = css({
  background: "#EDEDED",
});

export const ArrowImage = styled("img", {
  border: "0",
  margin: "0",
  padding: "0px 0px 0px 5px",

  variants: {
    /**
     * The arrow for a column that isn't sorted yet. It stays in the DOM and
     * only fades in on hover, so the header doesn't reflow under the pointer;
     * it points the way the next click will sort, matching the tooltip text.
     */
    preview: {
      true: {
        opacity: 0,
        transition: "opacity 120ms ease",

        [`${ArrowContent}:hover &`]: {
          opacity: 1,
        },
      },
    },
  },
});

/**
 * Lays a header cell's label and the header extras (see
 * `TableHeaderExtrasContext`) side by side inside the last `<th>`, label left
 * and extras right, at the cell's own height — no extra band above the table.
 */
export const HeaderCellWithExtras = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  minWidth: "max-content",
});

/** The extras themselves; clicks here must not reach a sortable header. */
export const HeaderExtras = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  marginLeft: "auto",
});
